import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserPlus, Check, X, Search, MessageCircle } from "lucide-react";

export default function Friends() {
  const { user } = useAuth();
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const load = () => api.get("/friends").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/users/search", { params: { q } }); setResults(data); } catch (err) { console.error("Friend search failed:", err); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const request = async (target_id) => {
    try {
      await api.post("/friends/request", { target_id });
      toast.success("Friend request sent");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const accept = async (request_id) => {
    try { await api.post("/friends/accept", { request_id }); toast.success("Friend added"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const decline = async (request_id) => {
    try { await api.post("/friends/decline", { request_id }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!user) return <Navigate to="/login" />;
  const pendingIds = new Set([...data.outgoing.map((f) => f.target_id), ...data.friends.map((f) => f.id)]);

  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <div className="mono text-xs text-primary">// social · friends</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">Your network.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Friends see each other's Updates and can exchange direct messages including Learn videos.</p>

      <div className="mt-8">
        <div className="flex items-center gap-2 border border-border px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input data-testid="friend-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people to add…" className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground" />
        </div>
        {results.length > 0 && (
          <div className="mt-2 border border-border">
            {results.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-3 border-b border-border last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center bg-primary/20 text-xs font-bold text-primary">{u.display_name?.[0]?.toUpperCase()}</div>
                  <div>
                    <div className="font-bold text-sm">{u.display_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{u.role} · {u.points ?? 0} pts</div>
                  </div>
                </div>
                {pendingIds.has(u.id) ? (
                  <span className="text-xs text-muted-foreground uppercase tracking-widest">pending</span>
                ) : (
                  <Button data-testid={`add-${u.id}`} size="sm" onClick={() => request(u.id)} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><UserPlus className="mr-2 h-3 w-3" />Add</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {data.incoming.length > 0 && (
        <div className="mt-10">
          <div className="mb-3 text-xs uppercase tracking-widest text-primary">Incoming requests</div>
          <div className="border border-border">
            {data.incoming.map((f) => (
              <div key={f.id} className="flex items-center justify-between p-3 border-b border-border last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center bg-primary/20 text-xs font-bold text-primary">{f.sender_name?.[0]?.toUpperCase()}</div>
                  <div className="font-bold text-sm">{f.sender_name}</div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`accept-${f.id}`} size="sm" onClick={() => accept(f.id)} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Check className="h-3 w-3" /></Button>
                  <Button data-testid={`decline-${f.id}`} size="sm" variant="outline" onClick={() => decline(f.id)} className="rounded-none"><X className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Friends ({data.friends.length})</div>
        {data.friends.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No friends yet — search above to send a request.</div>
        ) : (
          <div className="border border-border">
            {data.friends.map((f) => (
              <div key={f.friendship_id} data-testid={`friend-${f.id}`} className="flex items-center justify-between p-3 border-b border-border last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center bg-primary/20 text-xs font-bold text-primary">{f.display_name?.[0]?.toUpperCase()}</div>
                  <div className="font-bold text-sm">{f.display_name}</div>
                </div>
                <Link to={`/messages?to=${f.id}&name=${encodeURIComponent(f.display_name)}`}>
                  <Button data-testid={`dm-${f.id}`} size="sm" variant="outline" className="rounded-none"><MessageCircle className="mr-2 h-3 w-3" />Message</Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {data.outgoing.length > 0 && (
        <div className="mt-10">
          <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Sent · pending</div>
          <div className="border border-border">
            {data.outgoing.map((f) => (
              <div key={f.id} className="flex items-center justify-between p-3 border-b border-border last:border-b-0 text-sm">
                <span>{f.target_name}</span>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">waiting…</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
